import { ChangeEvent, Dispatch, useState } from 'react'
import { ExpandLess, ExpandMore } from '@mui/icons-material'
import {
  Divider,
  List,
  ListItemButton,
  Collapse,
  ListItemIcon,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { Selected, Field, Action, ActionType, RowPropertyName } from '../../types'
import { getSelectedInCurrentList } from '../../utils'
import { FilteredColumns } from '../filteredColumns'
import * as css from './styles'
import { TitleLabel } from './titleLabel'

interface Props {
  title: string
  fields: Field[]
  expanded: boolean
  searchResults: Field[]
  searchTerm: string
  selected: Selected
  maxListHeight: string
  handleSelectAll: () => void
  handleExpand: () => void
  dispatch: Dispatch<Action>
  setNoChange: () => void
}

export const ColumnList = ({
  title,
  fields,
  expanded,
  searchResults,
  searchTerm,
  selected,
  maxListHeight,
  handleSelectAll,
  handleExpand,
  dispatch,
  setNoChange,
}: Props) => {
  const [expandDisabled, setExpandDisabled] = useState<boolean>(false)

  const selectedInCurrentList = getSelectedInCurrentList({ selected, fields, searchResults })

  const selectedValues = Object.values(selectedInCurrentList)
  const allUnchecked = selectedValues.every(val => !val)
  const allChecked = selectedValues?.length > 0 && selectedValues.every(Boolean)
  const indeterminate = allUnchecked ? false : !allChecked

  return (
    <List css={css.list} data-testid="column-list">
      <ListItemButton
        css={[css.flex, css.listItemButton]}
        disableRipple
        onClick={handleClickExpand}
        data-testid="list-item-button"
      >
        <ListItemIcon>
          <FormControlLabel
            css={css.titleLabel}
            label={<TitleLabel title={title} />}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            data-testid="form-control-label"
            control={
              <Checkbox
                color="secondary"
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={handleSelectAll}
                edge="start"
                disableRipple
                data-testid="checkbox"
              />
            }
          />
        </ListItemIcon>
        {expanded ? (
          <ExpandLess color="disabled" data-testid="expand-less-icon" />
        ) : (
          <ExpandMore color="disabled" data-testid="expand-more-icon" />
        )}
      </ListItemButton>
      {expanded && <Divider />}
      <Collapse in={expanded} timeout="auto" data-testid="collapse">
        <css.StyledList height={maxListHeight} css={css.list}>
          {searchTerm ? (
            searchResults.length > 0 && (
              <FilteredColumns
                fields={searchResults}
                selected={selected}
                handleSelect={handleSelect}
                closePopover={closePopover}
              />
            )
          ) : (
            <FilteredColumns
              fields={fields}
              selected={selected}
              handleSelect={handleSelect}
              closePopover={closePopover}
            />
          )}
        </css.StyledList>
      </Collapse>
    </List>
  )

  function handleMouseOver(): void {
    setExpandDisabled(true)
  }

  function handleMouseOut(): void {
    setExpandDisabled(false)
  }

  function handleClickExpand(): void {
    if (expandDisabled) return

    handleExpand()
  }

  function closePopover() {
    dispatch({
      type: ActionType.setPopoverOpen,
      payload: false,
    })
  }

  function handleSelect({
    e,
    fieldId,
    rowPropertyName,
  }: {
    e: ChangeEvent<HTMLInputElement>
    fieldId: number
    rowPropertyName?: RowPropertyName
  }): void {
    const { checked } = e.target

    dispatch({
      type: ActionType.setSelected,
      payload: { fieldId, checked, rowPropertyName },
    })

    // Pendo event tracking logic
    setNoChange()
  }
}
